import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { usePaymentMethods } from '../../payment-methods/api/usePaymentMethods';
import { useSettings } from '../../settings/api/useSettings';
import { ContactPicker } from '../../../shared/ui/ContactPicker';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { useOnline } from '../../../shared/pwa/useOnline';
import { useCreateSale, useLastTradeRate } from '../api/useTrades';
import { decimal, tradeQuote, validAmount } from '../lib/quote';
import { AMOUNT_RE, RATE_RE, normalizeDecimal } from '../../../shared/lib/numericString';

const schema = z.object({
  deliveredCurrencyId: z.string().uuid({ message: 'form.required' }),
  deliveredAmount: z.string().trim().regex(AMOUNT_RE, { message: 'openings.quantity_invalid' }),
  paymentCurrencyId: z.string().uuid({ message: 'form.required' }),
  rate: z.string().trim().regex(RATE_RE, { message: 'openings.avg_cost_invalid' }),
  paymentBaseRate: z
    .string()
    .trim()
    .regex(RATE_RE, { message: 'openings.avg_cost_invalid' })
    .optional()
    .or(z.literal('')),
  immediatePayment: z
    .string()
    .trim()
    .regex(AMOUNT_RE, { message: 'openings.quantity_invalid' })
    .optional()
    .or(z.literal('')),
  paymentMethodId: z.string().uuid().optional().or(z.literal('')),
  paymentMethodNote: z.string().max(500).optional(),
  contactId: z.string().uuid().optional().or(z.literal('')),
  recipientName: z.string().max(200).optional(),
  destination: z.string().max(500).optional(),
  reference: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  transactionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'openings.date_invalid' }),
});

type FormValues = z.infer<typeof schema>;

export function SaleFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const online = useOnline();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [rateSanityDismissed, setRateSanityDismissed] = useState(false);

  const currencies = useCurrencies();
  const methods = usePaymentMethods();
  const settings = useSettings();
  const create = useCreateSale();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      deliveredCurrencyId: '',
      deliveredAmount: '',
      paymentCurrencyId: '',
      rate: '',
      paymentBaseRate: '',
      immediatePayment: '',
      paymentMethodId: '',
      paymentMethodNote: '',
      contactId: '',
      recipientName: '',
      destination: '',
      reference: '',
      notes: '',
      transactionDate: new Date().toISOString().slice(0, 10),
    },
  });

  const watched = useWatch({ control });
  const deliveredCurrencyId = watched.deliveredCurrencyId;
  const paymentCurrencyId = watched.paymentCurrencyId;
  const deliveredAmount = watched.deliveredAmount ?? '';
  const rate = watched.rate ?? '';
  const paymentBaseRate = watched.paymentBaseRate ?? '';
  const immediatePayment = watched.immediatePayment ?? '';
  const paymentMethodId = watched.paymentMethodId ?? '';

  const deliveredCurrency = currencies.data?.find((c) => c.id === deliveredCurrencyId);
  const paymentCurrency = currencies.data?.find((c) => c.id === paymentCurrencyId);
  const selectedMethod = methods.data?.find((m) => m.id === paymentMethodId);
  const baseCurrencyId = settings.data?.baseCurrencyId;
  const deliveredIsBase = !!baseCurrencyId && deliveredCurrencyId === baseCurrencyId;
  const paymentIsBase = !!baseCurrencyId && paymentCurrencyId === baseCurrencyId;

  useEffect(() => {
    setValue('rate', '');
    setValue('paymentBaseRate', '');
    setValue('immediatePayment', '');
    setRateSanityDismissed(false);
  }, [deliveredCurrencyId, paymentCurrencyId, setValue]);

  const lastRate = useLastTradeRate(
    deliveredCurrencyId || undefined,
    paymentCurrencyId || undefined,
  );

  const quote = tradeQuote({
    amount: deliveredAmount,
    rate,
    paymentBaseRate,
    deliveredIsBase,
    paymentIsBase,
    paymentDp: paymentCurrency?.decimalPlaces ?? 2,
  });
  const derivedTotal = quote?.paymentTotal ?? null;
  const validRate = RATE_RE.test(rate) && decimal(rate).gt(0);
  const showRateWarning =
    !rateSanityDismissed &&
    !!lastRate &&
    validRate &&
    decimal(lastRate).gt(0) &&
    (decimal(rate).gt(decimal(lastRate).times(3)) || decimal(rate).lt(decimal(lastRate).div(3)));
  const immPay = validAmount(immediatePayment) ? decimal(immediatePayment) : decimal('0');
  const remaining = derivedTotal
    ? decimal(derivedTotal)
        .minus(immPay)
        .toFixed(paymentCurrency?.decimalPlaces ?? 2)
    : '0';

  async function onSubmit(values: FormValues) {
    if (!baseCurrencyId || !quote || 'error' in quote) {
      setError('root', { message: quote && 'error' in quote ? quote.error : 'form.required' });
      return;
    }
    if (values.deliveredCurrencyId === values.paymentCurrencyId) {
      setError('paymentCurrencyId', { message: 'trades.different_currencies' });
      return;
    }
    if (immPay.gt(quote.paymentTotal)) {
      setError('immediatePayment', { message: 'trades.overpayment' });
      return;
    }

    const input = {
      deliveredCurrencyId: values.deliveredCurrencyId,
      deliveredAmount: normalizeDecimal(values.deliveredAmount),
      paymentCurrencyId: values.paymentCurrencyId,
      ...quote,
      ...(values.immediatePayment
        ? { immediatePayment: normalizeDecimal(values.immediatePayment) }
        : {}),
      ...(values.paymentMethodId ? { paymentMethodId: values.paymentMethodId } : {}),
      ...(values.paymentMethodNote ? { paymentMethodNote: values.paymentMethodNote } : {}),
      ...(values.contactId ? { contactId: values.contactId } : {}),
      ...(values.recipientName ? { recipientName: values.recipientName } : {}),
      ...(values.destination ? { destination: values.destination } : {}),
      ...(values.reference ? { reference: values.reference } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
      transactionDate: new Date(values.transactionDate).toISOString(),
    };
    const result = await create.mutateAsync({ input, idempotencyKey });
    setIdempotencyKey(crypto.randomUUID());
    navigate(`/sales/${result.id}`);
  }

  const fromCode = deliveredCurrency?.code ?? '—';
  const toCode = paymentCurrency?.code ?? '—';
  const baseCode = currencies.data?.find((c) => c.id === baseCurrencyId)?.code ?? '—';

  return (
    <>
      <PageHeader title={t('sales.new')} />

      <form className="form trade-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Delivered currency */}
        <div className="field">
          <label htmlFor="sf-delivered-currency">{t('sales.from_currency')}</label>
          <select id="sf-delivered-currency" {...register('deliveredCurrencyId')}>
            <option value="">{t('common.choose')}</option>
            {currencies.data
              ?.filter((c) => c.isActive && c.id !== paymentCurrencyId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          {errors.deliveredCurrencyId ? <p className="field__error">{t('form.required')}</p> : null}
        </div>

        {/* Delivered amount */}
        <div className="field">
          <label htmlFor="sf-delivered-amount">{t('sales.delivered_amount')}</label>
          <input
            id="sf-delivered-amount"
            {...register('deliveredAmount')}
            inputMode="decimal"
            placeholder="0.00"
            aria-invalid={!!errors.deliveredAmount}
          />
          {errors.deliveredAmount ? (
            <p className="field__error">{t('openings.quantity_invalid')}</p>
          ) : null}
        </div>

        {/* Payment currency */}
        <div className="field">
          <label htmlFor="sf-payment-currency">{t('sales.to_currency')}</label>
          <select id="sf-payment-currency" {...register('paymentCurrencyId')}>
            <option value="">{t('common.choose')}</option>
            {currencies.data
              ?.filter((c) => c.isActive && c.id !== deliveredCurrencyId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          {errors.paymentCurrencyId ? <p className="field__error">{t('form.required')}</p> : null}
        </div>

        <div className="field">
          <label htmlFor="sf-rate">{t('trades.pair_rate', { from: fromCode, to: toCode })}</label>
          <input
            id="sf-rate"
            {...register('rate')}
            inputMode="decimal"
            placeholder="0.00"
            aria-invalid={!!errors.rate}
            dir="ltr"
          />
          {errors.rate ? <p className="field__error">{t('openings.avg_cost_invalid')}</p> : null}
          {validRate ? (
            <p dir="ltr">
              1 {fromCode} = {normalizeDecimal(rate)} {toCode}
            </p>
          ) : null}
        </div>

        {deliveredCurrencyId && paymentCurrencyId && !deliveredIsBase && !paymentIsBase ? (
          <div className="field">
            <label htmlFor="sf-payment-rate">
              {t('trades.reporting_rate', { code: toCode, base: baseCode })}
            </label>
            <input
              id="sf-payment-rate"
              {...register('paymentBaseRate')}
              inputMode="decimal"
              placeholder="0.00"
              dir="ltr"
              aria-describedby="sf-reporting-help"
            />
            <p id="sf-reporting-help" className="field__hint">
              {t('trades.reporting_help', { base: baseCode })}
            </p>
          </div>
        ) : null}
        {quote && 'error' in quote ? (
          <p className="field__error" role="alert">
            {t(quote.error ?? 'form.required')}
          </p>
        ) : null}

        {derivedTotal ? (
          <div className="summary-row-list" aria-live="polite">
            <p>
              {t('sales.preview_receive')}:{' '}
              <bdi>
                {derivedTotal} {toCode}
              </bdi>
            </p>
            <p>
              {t('purchases.paid_now')}:{' '}
              <bdi>
                {immPay.toFixed(paymentCurrency?.decimalPlaces ?? 2)} {toCode}
              </bdi>
            </p>
            <p>
              {t('purchases.remaining_due')}:{' '}
              <bdi>
                {remaining} {toCode}
              </bdi>
            </p>
          </div>
        ) : null}

        {/* Reversed-rate sanity warning */}
        {showRateWarning && lastRate ? (
          <div className="notice notice--warn" role="alert">
            {t('sales.rate_warning', {
              from: fromCode,
              last: decimal(lastRate).toFixed(4),
              to: toCode,
            })}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setRateSanityDismissed(true)}
            >
              {t('sales.rate_warning_dismiss')}
            </button>
          </div>
        ) : null}

        {/* Immediate payment */}
        <div className="field">
          <label htmlFor="sf-immediate">
            {t('sales.immediate_payment')} ({toCode})
          </label>
          <input
            id="sf-immediate"
            {...register('immediatePayment')}
            inputMode="decimal"
            placeholder="0.00"
          />
        </div>

        {immPay.gt(0) ? (
          <div className="field">
            <label htmlFor="sf-method">{t('sales.payment_method')}</label>
            <select id="sf-method" {...register('paymentMethodId')}>
              <option value="">{t('common.choose')}</option>
              {methods.data
                ?.filter((m) => m.isActive)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.labelFr}
                  </option>
                ))}
            </select>
            {selectedMethod?.requiresNote ? (
              <div className="field">
                <label htmlFor="sf-method-note">{t('sales.method_note')}</label>
                <textarea id="sf-method-note" {...register('paymentMethodNote')} rows={2} />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Contact (optional) */}
        <div className="field">
          <label htmlFor="sf-contact">{t('sales.contact_optional')}</label>
          <Controller
            name="contactId"
            control={control}
            render={({ field }) => (
              <ContactPicker
                id="sf-contact"
                value={field.value ?? ''}
                onChange={field.onChange}
                allowNone
              />
            )}
          />
        </div>

        {/* Sale-specific: recipient and destination */}
        <div className="field">
          <label htmlFor="sf-recipient">{t('sales.recipient_name')}</label>
          <input id="sf-recipient" {...register('recipientName')} maxLength={200} />
        </div>
        <div className="field">
          <label htmlFor="sf-destination">{t('sales.destination')}</label>
          <input id="sf-destination" {...register('destination')} maxLength={500} />
        </div>

        {/* Reference */}
        <div className="field">
          <label htmlFor="sf-ref">{t('sales.reference')}</label>
          <input id="sf-ref" {...register('reference')} maxLength={200} />
        </div>

        {/* Notes */}
        <div className="field">
          <label htmlFor="sf-notes">{t('sales.notes')}</label>
          <textarea id="sf-notes" {...register('notes')} rows={3} maxLength={2000} />
        </div>

        {/* Transaction date */}
        <div className="field">
          <label htmlFor="sf-date">{t('sales.date')}</label>
          <input id="sf-date" type="date" {...register('transactionDate')} />
        </div>

        {errors.immediatePayment ? (
          <p className="field__error" role="alert">
            {t(errors.immediatePayment.message ?? 'openings.quantity_invalid')}
          </p>
        ) : null}
        {errors.root?.message ? (
          <p className="field__error" role="alert">
            {t(errors.root.message)}
          </p>
        ) : null}
        {currencies.error ? <ErrorMessage error={currencies.error} /> : null}
        {settings.error ? <ErrorMessage error={settings.error} /> : null}
        {create.error ? <ErrorMessage error={create.error} /> : null}

        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/sales')}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={isSubmitting || !online}
            title={!online ? t('sales.offline_submit') : undefined}
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </>
  );
}
